import { motion } from 'framer-motion';
import {
  Activity, MessageCircleHeart, FileText, BookOpen,
  Moon, Droplets, Shield, Sparkles
} from 'lucide-react';

const features = [
  {
    icon: Activity,
    title: 'Rastreamento de Sintomas',
    description: 'Registre e monitore ondas de calor, insônia, alterações de humor e mais de 10 sintomas diariamente.',
    size: 'col-span-12 md:col-span-7',
    highlight: false,
    color: 'text-rose-500 bg-rose-50',
  },
  {
    icon: MessageCircleHeart,
    title: 'Chat com IA 24/7',
    description: 'Tire dúvidas sobre menopausa a qualquer hora com nossa IA especializada. Como ter uma amiga médica sempre disponível.',
    size: 'col-span-12 md:col-span-5',
    highlight: true,
    color: 'text-[#B54182] bg-[#FAADC7]/20',
  },
  {
    icon: FileText,
    title: 'Relatórios Avançados',
    description: 'Gere relatórios detalhados para levar ao seu médico. Suas consultas ficarão muito mais produtivas.',
    size: 'col-span-12 md:col-span-5',
    highlight: true,
    color: 'text-emerald-500 bg-emerald-50',
  },
  {
    icon: BookOpen,
    title: 'Conteúdo Educativo',
    description: 'Artigos confiáveis sobre menopausa, hormônios, alimentação e exercícios escritos por especialistas.',
    size: 'col-span-12 md:col-span-7',
    highlight: false,
    color: 'text-amber-500 bg-amber-50',
  },
  {
    icon: Moon,
    title: 'Rastreador de Sono',
    description: 'Monitore suas horas e qualidade de sono para identificar padrões e melhorar seu descanso.',
    size: 'col-span-12 md:col-span-4',
    highlight: false,
    color: 'text-indigo-500 bg-indigo-50',
  },
  {
    icon: Droplets,
    title: 'Hidratação Diária',
    description: 'Acompanhe seu consumo de água e mantenha-se hidratada durante o dia.',
    size: 'col-span-12 md:col-span-4',
    highlight: false,
    color: 'text-cyan-500 bg-cyan-50',
  },
  {
    icon: Shield,
    title: 'Privacidade Total',
    description: 'Seus dados são seus. Segurança e privacidade são nossa prioridade absoluta.',
    size: 'col-span-12 md:col-span-4',
    highlight: false,
    color: 'text-stone-600 bg-stone-100',
  },
];

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function FeaturesSection() {
  return (
    <section
      id="funcionalidades"
      data-testid="features-section"
      className="py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FAADC7] to-transparent" />

      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="text-sm font-semibold text-[#B54182] uppercase tracking-widest mb-4 block">
            Funcionalidades
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-stone-900 mb-6"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Tudo que você precisa,{' '}
            <span className="text-[#B54182]">em um só lugar</span>
          </h2>
          <p className="text-base md:text-lg text-stone-500">
            Ferramentas pensadas especialmente para mulheres na menopausa, combinando tecnologia e empatia.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-12 gap-4 md:gap-6"
        >
          {features.map((feature) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={feature.title}
                variants={staggerItem}
                whileHover={{ y: -5 }}
                className={`${feature.size} ${
                  feature.highlight
                    ? 'text-white'
                    : 'bg-white border border-stone-100'
                } rounded-3xl p-6 md:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 group cursor-default`}
                style={feature.highlight ? { background: 'linear-gradient(135deg, #B54182, #BE5791)' } : undefined}
                data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className={`w-12 h-12 rounded-xl ${
                  feature.highlight ? 'bg-white/20 text-white' : feature.color
                } flex items-center justify-center mb-4`}>
                  <Icon size={24} />
                </div>
                <h3
                  className={`text-lg md:text-xl font-bold mb-2 ${feature.highlight ? 'text-white' : 'text-stone-900'}`}
                  style={{ fontFamily: 'var(--font-heading)' }}
                >
                  {feature.title}
                </h3>
                <p className={`text-sm md:text-base leading-relaxed ${feature.highlight ? 'text-[#FAADC7]' : 'text-stone-500'}`}>
                  {feature.description}
                </p>
                {feature.highlight && (
                  <div className="mt-4 flex items-center gap-2">
                    <Sparkles size={16} className="text-[#FAADC7]" />
                    <span className="text-sm font-medium text-[#FAADC7]">Funcionalidade Premium</span>
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
